import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { REVIEWS_SEED, SERVICE_TICKETS_SEED } from '@common/data';
import type {
  Review,
  ServiceTicket,
  ServiceTicketActionEntry,
  ServiceTicketAssignee,
  ServiceTicketBoard,
  ServiceTicketCounts,
  ServiceTicketDraft,
  ServiceTicketNote,
  ServiceTicketPage,
  ServiceTicketQuery,
  TicketExport,
  TicketInboundMessage,
  TicketResolution,
  TicketResolutionPayload,
  TicketResponseEvent,
  TicketSlaBreachEvent,
  TicketSubmission,
} from '@common/types';
import { Observable, defer, of } from 'rxjs';
import { ticketResolutionErrors } from '../functions/ticket-resolution.function';
import { AccountAccessService } from './account-access.service';
import { AccountSettingsService } from './account-settings.service';
import { CurrentUserService } from './current-user.service';
import { NotificationInboxService } from './notification-inbox.service';
import { OrganizationService } from './organization.service';
import { ScopeService } from './scope.service';
import { ServiceTicketStatusesService } from './service-ticket-statuses.service';
import { TicketDirectoryService } from './ticket-directory.service';
import { TicketPolicyService } from './ticket-policy.service';
import { UserSettingsService } from './user-settings.service';

/** In-memory adapter. Operations are atomic within this session; the server must enforce the same contracts. */
@Injectable({ providedIn: 'root' })
export class ServiceTicketsService {
  private readonly injector = inject(Injector);
  private readonly currentUser = inject(CurrentUserService);
  private readonly preferences = inject(UserSettingsService);
  private readonly scope = inject(ScopeService);
  private readonly access = inject(AccountAccessService);
  private readonly organizations = inject(OrganizationService);
  private readonly settings = inject(AccountSettingsService);
  private readonly policies = inject(TicketPolicyService);
  private readonly directory = inject(TicketDirectoryService);
  private readonly tickets = signal<ServiceTicket[]>(
    structuredClone(SERVICE_TICKETS_SEED) as ServiceTicket[],
  );
  private readonly changed = signal(0);
  readonly all = this.tickets.asReadonly();
  readonly revision = this.changed.asReadonly();
  readonly context = computed(() => ({
    organizationId: this.organizations.activeId() ?? '',
    accountIds: this.scope
      .activeAccounts()
      .filter(
        (a) =>
          this.can(a.id, 'view') &&
          this.organizations
            .accountsFor(this.organizations.activeId() ?? '')
            .some((p) => p.id === a.id),
      )
      .map((a) => a.id),
  }));

  list(query: ServiceTicketQuery): Observable<ServiceTicketPage> {
    return defer(() => {
      const rows = this.filtered(query);
      const field = query.sortField;
      if (field)
        rows.sort((a, b) => {
          const value = (t: ServiceTicket): string | number =>
            field === 'assignee'
              ? (t.assignee?.name ?? '')
              : ((t[field as keyof ServiceTicket] as string | number) ?? '');
          return (
            (value(a) < value(b) ? -1 : value(a) > value(b) ? 1 : 0) *
            (query.sortOrder === 'desc' ? -1 : 1)
          );
        });
      return of({
        data: rows.slice(
          Math.max(0, query.first),
          Math.max(0, query.first) + Math.max(1, query.rows),
        ),
        total: rows.length,
      });
    });
  }

  board(
    query: Pick<ServiceTicketQuery, 'search' | 'filters' | 'scope' | 'dateRange'>,
  ): Observable<ServiceTicketBoard> {
    return defer(() => {
      const rows = this.filtered({
        first: 0,
        rows: Number.MAX_SAFE_INTEGER,
        sortField: null,
        sortOrder: null,
        ...query,
      });
      const definitions = this.statuses()
        .all()
        .filter((s) => s.isEnabled || rows.some((t) => t.status === s.key));
      return of({
        columns: definitions.map((s) => ({
          status: s.key,
          tickets: rows
            .filter((t) => t.status === s.key)
            .sort((a, b) => a.actionBefore.localeCompare(b.actionBefore)),
        })),
        total: rows.length,
      });
    });
  }

  counts(
    scope: ServiceTicketQuery['scope'] = 'all',
    dateRange: ServiceTicketQuery['dateRange'] = null,
    filters: Record<string, string[]> = {},
    search = '',
  ): Observable<ServiceTicketCounts> {
    return defer(() => {
      const rows = this.filtered({
        first: 0,
        rows: Number.MAX_SAFE_INTEGER,
        sortField: null,
        sortOrder: null,
        scope,
        dateRange,
        filters,
        search,
      });
      const now = Date.now();
      const open = rows.filter((t) => !this.statuses().isTerminal(t.status));
      const resolved = rows.filter((t) => t.resolvedAt);
      return of({
        total: rows.length,
        open: open.length,
        escalated: open.filter((t) => t.urgency !== 'normal').length,
        dueToday: open.filter(
          (t) => Date.parse(t.actionBefore) >= now && Date.parse(t.actionBefore) <= now + 86400000,
        ).length,
        overdue: open.filter((t) => Date.parse(t.actionBefore) < now).length,
        resolvedThisWeek: resolved.filter(
          (t) =>
            Date.parse(t.resolvedAt!) >= now - 7 * 86400000 && Date.parse(t.resolvedAt!) <= now,
        ).length,
        avgResolutionHours: resolved.length
          ? Math.round(
              resolved.reduce(
                (sum, t) =>
                  sum +
                  Math.max(0, (Date.parse(t.resolvedAt!) - Date.parse(t.createdAt)) / 3600000),
                0,
              ) / resolved.length,
            )
          : 0,
      });
    });
  }

  getById(id: string): Observable<ServiceTicket | undefined> {
    return defer(() => of(this.visible(id)));
  }
  getReview(id: string | null): Observable<Review | undefined> {
    return of(REVIEWS_SEED.find((r) => r.id === id));
  }
  related(ticket: ServiceTicket): ServiceTicket[] {
    return this.tickets().filter(
      (t) => t.caseId === ticket.caseId && t.id !== ticket.id && this.allowed(t),
    );
  }
  unread(ticket: ServiceTicket): number {
    const read = ticket.readMessageIdsByUser[this.currentUser.id()] ?? [];
    return ticket.inboundMessages.filter((m) => !read.includes(m.messageId)).length;
  }
  effectivePolicy(ticket: Pick<ServiceTicket, 'organizationId'>) {
    return this.policies.forOrganization(ticket.organizationId);
  }
  resolutionErrors(
    ticket: ServiceTicket,
    payload: TicketResolutionPayload | undefined,
    closing: boolean,
  ): string[] {
    return ticketResolutionErrors(payload, this.effectivePolicy(ticket), closing, ticket.createdAt);
  }

  create(draft: ServiceTicketDraft): Observable<ServiceTicket> {
    return defer(() => of(this.createNow(draft)));
  }

  private createNow(draft: ServiceTicketDraft, submission?: TicketSubmission): ServiceTicket {
    const parent = draft.parentTicketId ? this.require(draft.parentTicketId, 'view') : null;
    const context = this.context();
    const accountId =
      parent?.accountId ??
      draft.accountId ??
      (context.accountIds.length === 1 ? context.accountIds[0] : '');
    const organizationId = parent?.organizationId ?? draft.organizationId ?? context.organizationId;
    if (organizationId !== context.organizationId || !context.accountIds.includes(accountId))
      throw new Error('Select one accessible property in the topbar before creating a ticket.');
    if (!this.can(accountId, 'create'))
      throw new Error('You do not have permission to create tickets for this property.');
    if (draft.propertyId && draft.propertyId !== accountId && !parent)
      throw new Error('The originating property must match the selected account.');
    if (this.statuses().isTerminal(draft.status))
      throw new Error('Create the ticket in an open status, then complete its resolution.');
    this.validateStatus(draft.status);
    const workflow = parent?.workflow ?? draft.workflow ?? 'central_complaint';
    const queueId =
      draft.queueId ??
      (workflow === 'central_complaint'
        ? `${organizationId}-central`
        : `${organizationId}-${accountId}`);
    const queue = this.directory.forAccount(accountId).find((q) => q.id === queueId);
    if (!queue) throw new Error('Choose a queue authorized for this property.');
    const overrides = this.settings.ticketsFor(accountId);
    const eligibleOwners = this.directory
      .assignees(accountId)
      .filter((user) => queue.memberIds.includes(user.id));
    const ownerId =
      draft.assigneeId && !submission
        ? draft.assigneeId
        : ([draft.assigneeId, overrides.defaultAssigneeId, queue.defaultOwnerId].find((id) =>
            eligibleOwners.some((user) => user.id === id),
          ) ?? null);
    const assignee = this.assignee(accountId, ownerId);
    if (assignee && !queue.memberIds.includes(assignee.id))
      throw new Error('The assignee must belong to the handling queue.');
    const now = new Date().toISOString();
    const id = `TKT-${crypto.randomUUID()}`;
    const urgency = draft.urgency ?? 'normal';
    const policy = this.policies.forOrganization(organizationId);
    const sla = policy.slaTargets.find((s) => s.urgency === urgency)!;
    const actor =
      submission?.submittedBy ??
      (submission ? { id: 'system', name: 'Submission service', initials: 'SY' } : this.actor());
    const guestName = parent?.guestName ?? draft.guestName?.trim() ?? '';
    if (!guestName) throw new Error('Enter a guest name for this case.');
    const watcherIds = [
      ...new Set([
        ...draft.watcherIds,
        ...(!submission && this.preferences.tickets().autoWatchCreated
          ? [this.currentUser.id()]
          : []),
      ]),
    ];
    const ticket: ServiceTicket = {
      id,
      organizationId,
      accountId,
      propertyId: parent?.propertyId ?? draft.propertyId ?? accountId,
      workflow,
      intakeChannel: parent?.intakeChannel ?? draft.intakeChannel ?? 'letter',
      questionnaireId: parent?.questionnaireId ?? draft.questionnaireId ?? null,
      submissionId: parent?.submissionId ?? draft.submissionId ?? null,
      guestId: parent?.guestId ?? draft.guestId ?? null,
      guestName,
      guestEmail: parent?.guestEmail ?? draft.guestEmail?.trim() ?? '',
      guestPhone: parent?.guestPhone ?? draft.guestPhone?.trim() ?? '',
      bookingReference: parent?.bookingReference ?? draft.bookingReference?.trim() ?? '',
      initialFeedbackType: parent?.initialFeedbackType ?? draft.initialFeedbackType ?? null,
      issueCategories: draft.issueCategories ?? parent?.issueCategories ?? [],
      version: 1,
      updatedAt: now,
      createdAt: now,
      submittedBy: actor,
      queueId,
      parentTicketId: parent?.id ?? null,
      caseId: parent?.caseId ?? id,
      conversationId: parent ? null : (submission?.conversationId ?? null),
      inboundMessages: [],
      readMessageIdsByUser: {},
      needsResponse: false,
      handledMessageIds: [],
      lastGuestReplyAt: null,
      firstResponseDueAt: new Date(
        Date.parse(now) + sla.firstResponseHours * 3600000,
      ).toISOString(),
      actionBefore:
        draft.actionBefore ||
        new Date(Date.parse(now) + sla.resolutionHours * 3600000).toISOString(),
      escalationRecipientId: overrides.escalationRecipientId,
      escalationRoleLabel: policy.escalation.notifyLabel,
      escalationKeys: [],
      resolutionHistory: [],
      evidence: [],
      reviewId: null,
      subject:
        draft.subject.trim() ||
        `${guestName} — ${workflow === 'central_complaint' ? 'Complaint' : 'Service request'}`,
      status:
        assignee &&
        draft.status === 'new' &&
        this.statuses().byKey().get('assigned')?.isEnabled &&
        !this.statuses().isTerminal('assigned')
          ? 'assigned'
          : !assignee && this.statuses().byKey().get('new')?.isEnabled
            ? 'new'
            : draft.status,
      urgency,
      departmentId: draft.departmentId ?? null,
      assignee,
      rating: null,
      resolvedAt: null,
      watcherIds,
      isMine: assignee?.id === this.currentUser.id(),
      isWatching: watcherIds.includes(this.currentUser.id()),
      lastActionNote: this.preview(draft.note),
      actions: [{ id: crypto.randomUUID(), type: 'created', actor, at: now }],
      notes: this.preview(draft.note)
        ? [{ id: crypto.randomUUID(), author: actor, at: now, body: draft.note }]
        : [],
    };
    if (!Number.isFinite(Date.parse(ticket.actionBefore)))
      throw new Error('Enter a valid resolution deadline.');
    this.tickets.update((all) => [ticket, ...all]);
    this.bump();
    return ticket;
  }

  /** First matching enabled rule wins in policy order. One initial case per organization/account/submission. */
  processSubmission(event: TicketSubmission): Observable<ServiceTicket | null> {
    return defer(() => {
      if (!event.submissionId?.trim() || !event.questionnaireId?.trim())
        throw new Error('Stable submission and questionnaire IDs are required.');
      const existing = this.tickets().find(
        (t) =>
          !t.parentTicketId &&
          t.organizationId === event.organizationId &&
          t.accountId === event.accountId &&
          t.submissionId === event.submissionId &&
          t.questionnaireId === event.questionnaireId,
      );
      if (existing) {
        if (!this.allowed(existing)) throw new Error('Case is outside the active scope.');
        return of(existing);
      }
      const disabled = this.settings.ticketsFor(event.accountId).disabledAutoRuleIds;
      const rule = this.policies
        .forOrganization(event.organizationId)
        .autoTicketRules.find(
          (r) =>
            r.isEnabled &&
            !disabled.includes(r.id) &&
            r.questionnaireId === event.questionnaireId &&
            (r.triggerType === 'form_submission' ||
              (Number.isFinite(event.scores[r.questionId]) &&
                event.scores[r.questionId] <= r.threshold)),
        );
      if (!rule) return of(null);
      return of(
        this.createNow(
          {
            ...event,
            workflow: rule.workflow,
            note: event.body,
            status: 'new',
            assigneeId: rule.ownerId,
            queueId: this.directory
              .forAccount(event.accountId)
              .some((queue) => queue.id === rule.queueId)
              ? rule.queueId
              : null,
            departmentId: rule.departmentId,
            urgency: rule.urgency,
            watcherIds: [],
            actionBefore: '',
          },
          event,
        ),
      );
    });
  }

  updateTicket(id: string, draft: ServiceTicketDraft): Observable<ServiceTicket> {
    return defer(() => {
      const ticket = this.require(id);
      this.checkVersion(ticket, draft.expectedVersion);
      const assignee = this.assignee(ticket.accountId, draft.assigneeId);
      const queueId = draft.queueId === undefined ? ticket.queueId : draft.queueId;
      const queue = this.directory.forAccount(ticket.accountId).find((q) => q.id === queueId);
      if (!queue || (assignee && !queue.memberIds.includes(assignee.id)))
        throw new Error('Choose a valid queue and an authorized queue member.');
      const status = this.transitionValues(ticket, draft.status, ticket.resolution);
      const updated = {
        ...ticket,
        ...status,
        subject: draft.subject.trim(),
        departmentId: draft.departmentId ?? null,
        assignee,
        queueId,
        watcherIds: [...draft.watcherIds],
        actionBefore: draft.actionBefore,
        urgency: draft.urgency ?? ticket.urgency,
        guestName: draft.guestName?.trim() ?? ticket.guestName,
        guestEmail: draft.guestEmail?.trim() ?? ticket.guestEmail,
        guestPhone: draft.guestPhone?.trim() ?? ticket.guestPhone,
        bookingReference: draft.bookingReference?.trim() ?? ticket.bookingReference,
        initialFeedbackType: draft.initialFeedbackType ?? ticket.initialFeedbackType,
        issueCategories: draft.issueCategories ?? ticket.issueCategories,
        workflow: draft.workflow ?? ticket.workflow,
        intakeChannel: draft.intakeChannel ?? ticket.intakeChannel,
      };
      if (
        !updated.subject ||
        !updated.guestName ||
        !Number.isFinite(Date.parse(updated.actionBefore))
      )
        throw new Error('Subject, guest and a valid deadline are required.');
      return of(
        this.commit(
          ticket,
          updated,
          ticket.assignee?.id !== assignee?.id ? 'reassigned' : 'updated',
        ),
      );
    });
  }

  transition(
    id: string,
    status: string,
    payload?: TicketResolutionPayload,
    expectedVersion?: number,
  ): Observable<ServiceTicket> {
    return defer(() => {
      const ticket = this.require(id);
      this.checkVersion(ticket, expectedVersion);
      const next = this.transitionValues(ticket, status, payload ?? ticket.resolution);
      return of(
        this.commit(
          ticket,
          { ...ticket, ...next },
          this.statuses().isTerminal(status)
            ? 'resolved'
            : this.statuses().isTerminal(ticket.status)
              ? 'reopened'
              : 'status_changed',
        ),
      );
    });
  }
  updateStatus(id: string, status: string): Observable<ServiceTicket> {
    return this.transition(id, status);
  }

  private transitionValues(
    ticket: ServiceTicket,
    status: string,
    payload?: TicketResolutionPayload,
  ): Partial<ServiceTicket> {
    this.validateStatus(status, ticket.status);
    if (status === ticket.status) return {};
    if (this.statuses().isTerminal(status)) {
      const errors = this.resolutionErrors(ticket, payload, true);
      if (errors.length) throw new Error(errors.join(' '));
      const resolution: TicketResolution = {
        ...ticket.resolution,
        ...payload!,
        updatedAt: new Date().toISOString(),
        updatedBy: this.actor(),
      };
      const closedAt = resolution.dateOfResolution ?? resolution.updatedAt;
      return {
        status,
        resolvedAt: closedAt,
        resolution,
        resolutionHistory: [...ticket.resolutionHistory, { closedAt, status, resolution }],
      };
    }
    return {
      status,
      resolvedAt: null,
      ...(this.statuses().isTerminal(ticket.status) && ticket.resolution
        ? { resolution: { ...ticket.resolution, dateOfResolution: null } }
        : {}),
    };
  }

  updateResolution(id: string, payload: TicketResolutionPayload): Observable<TicketResolution> {
    return defer(() => {
      const ticket = this.require(id);
      if (this.statuses().isTerminal(ticket.status))
        throw new Error('Reopen the case before changing its resolution.');
      const errors = this.resolutionErrors(ticket, payload, false);
      if (errors.length) throw new Error(errors.join(' '));
      const resolution = {
        ...ticket.resolution,
        ...payload,
        updatedAt: new Date().toISOString(),
        updatedBy: this.actor(),
      };
      this.commit(ticket, { ...ticket, resolution }, 'resolution_saved');
      return of(resolution);
    });
  }

  claim(id: string, expectedVersion: number): Observable<ServiceTicket> {
    return defer(() => {
      const ticket = this.require(id);
      if (this.statuses().isTerminal(ticket.status))
        throw new Error('Reopen the ticket before claiming it.');
      this.checkVersion(ticket, expectedVersion);
      if (ticket.assignee && ticket.assignee.id !== this.currentUser.id())
        throw new Error(
          `Already claimed by ${ticket.assignee.name}. Refresh to see current ownership.`,
        );
      const queue = this.directory
        .forAccount(ticket.accountId)
        .find((q) => q.id === ticket.queueId);
      if (!queue?.memberIds.includes(this.currentUser.id()))
        throw new Error('You are not a member of this queue.');
      const assignee = this.assignee(ticket.accountId, this.currentUser.id());
      return of(
        this.commit(
          ticket,
          {
            ...ticket,
            assignee,
            status:
              ticket.status === 'new' &&
              this.statuses().byKey().get('assigned')?.isEnabled &&
              !this.statuses().isTerminal('assigned')
                ? 'assigned'
                : ticket.status,
          },
          'claimed',
        ),
      );
    });
  }

  escalate(id: string, reason = 'Escalated by handler'): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(id);
      if (this.statuses().isTerminal(t.status))
        throw new Error('Reopen the case before escalating it.');
      return of(
        this.commit(
          t,
          { ...t, urgency: t.urgency === 'normal' ? 'escalated' : 'critical' },
          'escalated',
          `${reason}. Recipient: ${t.escalationRecipientId ?? t.escalationRoleLabel}`,
        ),
      );
    });
  }

  addNote(id: string, body: string): Observable<ServiceTicketNote> {
    return defer(() => {
      const t = this.require(id);
      if (!this.preview(body)) throw new Error('Note body is required.');
      const note = {
        id: crypto.randomUUID(),
        author: this.actor(),
        at: new Date().toISOString(),
        body,
      };
      const watcherIds = this.preferences.tickets().autoWatchCommented
        ? [...new Set([...t.watcherIds, this.currentUser.id()])]
        : t.watcherIds;
      this.commit(
        t,
        { ...t, notes: [...t.notes, note], lastActionNote: this.preview(body), watcherIds },
        'note_added',
      );
      return of(note);
    });
  }

  setWatching(id: string, watching: boolean): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(id);
      const me = this.currentUser.id();
      return of(
        this.commit(
          t,
          {
            ...t,
            watcherIds: watching
              ? [...new Set([...t.watcherIds, me])]
              : t.watcherIds.filter((x) => x !== me),
          },
          'updated',
          watching ? 'Started watching' : 'Stopped watching',
        ),
      );
    });
  }

  receiveGuestReply(event: TicketInboundMessage): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(event.ticketId);
      if (t.parentTicketId) throw new Error('Guest communication belongs to the main case.');
      if (t.conversationId && t.conversationId !== event.conversationId)
        throw new Error('Conversation does not match this case.');
      if (
        !event.messageId ||
        !event.conversationId ||
        !Number.isFinite(Date.parse(event.receivedAt))
      )
        throw new Error('A valid message reference and timestamp are required.');
      if (t.inboundMessages.some((m) => m.messageId === event.messageId)) return of(t);
      const updated = this.commit(
        t,
        {
          ...t,
          conversationId: event.conversationId,
          inboundMessages: [...t.inboundMessages, event],
          lastGuestReplyAt:
            !t.lastGuestReplyAt || event.receivedAt > t.lastGuestReplyAt
              ? event.receivedAt
              : t.lastGuestReplyAt,
          needsResponse: true,
        },
        'guest_reply',
        event.messageId,
        { id: 'guest', name: t.guestName, initials: 'G' },
      );
      if (t.assignee?.id === this.currentUser.id())
        this.injector.get(NotificationInboxService).receive({
          id: `ticket-reply:${t.id}:${event.messageId}`,
          event: 'ticket.guest-reply',
          accountId: t.accountId,
          heading: `New reply on ${t.subject}`,
          body: event.preview,
          atIso: event.receivedAt,
          link: `/service-tickets/manage?ticket=${encodeURIComponent(t.id)}&tab=communications&account=${encodeURIComponent(t.accountId)}`,
          readAt: null,
        });
      return of(updated);
    });
  }

  markMessagesRead(id: string, messageIds: readonly string[]): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(id);
      const me = this.currentUser.id();
      return of(
        this.commit(
          t,
          {
            ...t,
            readMessageIdsByUser: {
              ...t.readMessageIdsByUser,
              [me]: [
                ...new Set([
                  ...(t.readMessageIdsByUser[me] ?? []),
                  ...messageIds.filter((id) => t.inboundMessages.some((m) => m.messageId === id)),
                ]),
              ],
            },
          },
          'updated',
          'Read guest messages',
        ),
      );
    });
  }

  markHandled(id: string, messageIds: readonly string[]): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(id);
      const handled = [
        ...new Set([
          ...t.handledMessageIds,
          ...messageIds.filter((id) => t.inboundMessages.some((m) => m.messageId === id)),
        ]),
      ];
      return of(
        this.commit(
          t,
          {
            ...t,
            handledMessageIds: handled,
            needsResponse: t.inboundMessages.some((m) => !handled.includes(m.messageId)),
          },
          'attention_handled',
        ),
      );
    });
  }

  addEvidence(id: string, name: string, url: string): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(id);
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' || parsed.username || parsed.password || !name.trim())
        throw new Error(
          'Provide a filename or description and an HTTPS evidence link without embedded credentials.',
        );
      return of(
        this.commit(
          t,
          {
            ...t,
            evidence: [
              ...t.evidence,
              {
                id: crypto.randomUUID(),
                name: name.trim(),
                url: parsed.href,
                kind: 'link',
                addedAt: new Date().toISOString(),
                addedBy: this.actor(),
              },
            ],
          },
          'evidence_added',
          name.trim(),
        ),
      );
    });
  }

  /** Consume a confirmed send from the communications service; this operation never sends mail. */
  recordResponse(event: TicketResponseEvent): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(event.ticketId);
      if (t.parentTicketId || !t.conversationId || t.conversationId !== event.conversationId)
        throw new Error('Response must belong to the main case conversation.');
      if (
        !Number.isFinite(Date.parse(event.sentAt)) ||
        Date.parse(event.sentAt) < Date.parse(t.createdAt)
      )
        throw new Error('Response timestamp is invalid.');
      if (t.actions.some((a) => a.type === 'comment' && a.note === event.messageId)) return of(t);
      const handled = [
        ...new Set([
          ...t.handledMessageIds,
          ...event.handledInboundMessageIds.filter((id) =>
            t.inboundMessages.some((m) => m.messageId === id),
          ),
        ]),
      ];
      const resolution: TicketResolution = t.resolution ?? {
        feedbackType: null,
        dateOfFirstReply: null,
        dateOfResolution: null,
        compensationGiven: false,
        compensationTypes: [],
        compensationValues: {},
        sentiment: null,
        mdCeoLetter: false,
        closingNotes: '',
        csat: null,
        updatedAt: event.sentAt,
        updatedBy: this.actor(),
      };
      return of(
        this.commit(
          t,
          {
            ...t,
            handledMessageIds: handled,
            needsResponse: t.inboundMessages.some((m) => !handled.includes(m.messageId)),
            resolution: {
              ...resolution,
              dateOfFirstReply:
                resolution.dateOfFirstReply && resolution.dateOfFirstReply < event.sentAt
                  ? resolution.dateOfFirstReply
                  : event.sentAt,
            },
          },
          'comment',
          event.messageId,
        ),
      );
    });
  }

  /** Server event adapter only: scheduling and delivery must run outside the browser. */
  applySlaBreach(event: TicketSlaBreachEvent): Observable<ServiceTicket> {
    return defer(() => {
      const t = this.require(event.ticketId);
      const deadline = event.target === 'first_response' ? t.firstResponseDueAt : t.actionBefore;
      const policy = this.effectivePolicy(t);
      const key = `${event.target}:${event.deadline}`;
      if (t.escalationKeys.includes(key)) return of(t);
      if (
        deadline !== event.deadline ||
        !Number.isFinite(Date.parse(event.occurredAt)) ||
        Date.parse(event.occurredAt) <
          Date.parse(deadline) + policy.escalation.afterBreachHours * 3600000
      )
        throw new Error('SLA event is stale or precedes the configured breach delay.');
      if (
        this.statuses().isTerminal(t.status) ||
        (event.target === 'first_response' && t.resolution?.dateOfFirstReply)
      )
        return of(t);
      return of(
        this.commit(
          t,
          {
            ...t,
            escalationKeys: [...t.escalationKeys, key],
            urgency: policy.escalation.raiseUrgency
              ? t.urgency === 'normal'
                ? 'escalated'
                : 'critical'
              : t.urgency,
          },
          'escalated',
          `${event.target} target breached. Recipient: ${t.escalationRecipientId ?? t.escalationRoleLabel}`,
          { id: 'system', name: 'SLA service', initials: 'SY' },
        ),
      );
    });
  }

  export(query: ServiceTicketQuery): Observable<TicketExport> {
    return defer(() => {
      const tickets = this.filtered(query);
      return of({
        schemaVersion: 1 as const,
        exportedAt: new Date().toISOString(),
        query: { ...query, ...this.context() },
        total: tickets.length,
        tickets,
      });
    });
  }

  private filtered(query: ServiceTicketQuery): ServiceTicket[] {
    const me = this.currentUser.id();
    const term = query.search.trim().toLowerCase();
    return this.tickets()
      .filter((t) => {
        if (
          !this.allowed(t) ||
          (query.organizationId && t.organizationId !== query.organizationId) ||
          (query.accountIds && !query.accountIds.includes(t.accountId))
        )
          return false;
        if (query.modifiedSince && t.updatedAt <= query.modifiedSince) return false;
        if (
          (query.scope === 'mine' && t.assignee?.id !== me) ||
          (query.scope === 'watching' && !t.watcherIds.includes(me)) ||
          (query.scope === 'unclaimed' && t.assignee) ||
          (query.scope === 'attention' && !t.needsResponse)
        )
          return false;
        if (
          query.scope === 'team' &&
          !this.directory.queues().some((q) => q.id === t.queueId && q.memberIds.includes(me))
        )
          return false;
        if (
          query.dateRange &&
          (t.createdAt < query.dateRange.from ||
            t.createdAt > `${query.dateRange.to}T23:59:59.999Z`)
        )
          return false;
        if (
          term &&
          ![
            t.id,
            t.subject,
            t.guestName,
            t.assignee?.name,
            t.lastActionNote,
            t.bookingReference,
          ].some((v) => v?.toLowerCase().includes(term))
        )
          return false;
        const values: Record<string, string> = {
          status: t.status,
          urgency: t.urgency,
          assignee: t.assignee?.id ?? 'unclaimed',
          department: t.departmentId ?? '',
          workflow: t.workflow,
          questionnaire: t.questionnaireId ?? '',
          queue: t.queueId ?? '',
          attention: t.needsResponse ? 'needs_response' : 'handled',
        };
        return Object.entries(query.filters).every(
          ([key, options]) => !options.length || options.includes(values[key] ?? ''),
        );
      })
      .map((t) => ({ ...t, isMine: t.assignee?.id === me, isWatching: t.watcherIds.includes(me) }));
  }

  private allowed(t: ServiceTicket): boolean {
    const c = this.context();
    return t.organizationId === c.organizationId && c.accountIds.includes(t.accountId);
  }
  private visible(id: string): ServiceTicket | undefined {
    return this.tickets().find((t) => t.id === id && this.allowed(t));
  }
  private require(id: string, action: 'view' | 'edit' = 'edit'): ServiceTicket {
    const t = this.visible(id);
    if (!t) throw new Error('Ticket is unavailable in your current account scope.');
    if (!this.can(t.accountId, action))
      throw new Error('You do not have permission to change tickets for this property.');
    return t;
  }
  private can(accountId: string, action: 'view' | 'create' | 'edit'): boolean {
    const permissions = this.access.roleFor(accountId)?.permissions['service-tickets'] ?? [];
    return permissions.includes(action) || permissions.includes('manage');
  }
  private statuses(): ServiceTicketStatusesService {
    return this.injector.get(ServiceTicketStatusesService);
  }
  private validateStatus(key: string, current?: string): void {
    const def = this.statuses().byKey().get(key);
    if (!def || (!def.isEnabled && key !== current)) throw new Error('Choose an enabled status.');
  }
  private assignee(accountId: string, id: string | null): ServiceTicketAssignee | null {
    if (!id) return null;
    const a = this.directory.assignees(accountId).find((a) => a.id === id);
    if (!a) throw new Error('The selected user is not an active member of this account.');
    return a;
  }
  private actor(): ServiceTicketAssignee {
    const name = this.currentUser.displayName() || 'Current user';
    return {
      id: this.currentUser.id(),
      name,
      initials: name
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase(),
    };
  }
  private checkVersion(t: ServiceTicket, expected?: number): void {
    if (expected !== undefined && expected !== t.version)
      throw new Error(
        `This case changed. Current owner: ${t.assignee?.name ?? 'Unclaimed'}. Refresh before trying again.`,
      );
  }
  private bump(): void {
    this.changed.update((n) => n + 1);
  }
  private preview(body: string): string | null {
    return (
      body
        ?.replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160) || null
    );
  }
  private commit(
    before: ServiceTicket,
    next: ServiceTicket,
    type: ServiceTicketActionEntry['type'],
    note?: string,
    actor = this.actor(),
  ): ServiceTicket {
    const at = new Date().toISOString();
    const changed = Object.keys(next).filter(
      (key) =>
        JSON.stringify(next[key as keyof ServiceTicket]) !==
        JSON.stringify(before[key as keyof ServiceTicket]),
    );
    const entry: ServiceTicketActionEntry = {
      id: crypto.randomUUID(),
      type,
      actor,
      at,
      note,
      before: Object.fromEntries(changed.map((k) => [k, before[k as keyof ServiceTicket]])),
      after: Object.fromEntries(changed.map((k) => [k, next[k as keyof ServiceTicket]])),
    };
    if (!entry.note) {
      const details: string[] = [];
      if (before.status !== next.status) details.push(`Status: ${before.status} → ${next.status}`);
      if (before.assignee?.id !== next.assignee?.id)
        details.push(
          `Owner: ${before.assignee?.name ?? 'Unclaimed'} → ${next.assignee?.name ?? 'Unclaimed'}`,
        );
      if (before.queueId !== next.queueId)
        details.push(`Queue: ${before.queueId ?? 'None'} → ${next.queueId ?? 'None'}`);
      if (before.actionBefore !== next.actionBefore)
        details.push(`Deadline: ${before.actionBefore} → ${next.actionBefore}`);
      if (type === 'resolved')
        details.push(
          `Resolution: ${this.preview(next.resolution?.closingNotes ?? '') ?? 'No closing note'}`,
        );
      entry.note = details.join('. ') || undefined;
    }
    const updated: ServiceTicket = {
      ...next,
      version: before.version + 1,
      updatedAt: at,
      isMine: next.assignee?.id === this.currentUser.id(),
      isWatching: next.watcherIds.includes(this.currentUser.id()),
      actions: [...before.actions, entry],
    };
    this.tickets.update((all) => all.map((t) => (t.id === before.id ? updated : t)));
    this.bump();
    return updated;
  }
}
